import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trashAPI } from '../services/api';
import { getLogoSrc } from '../utils/logoHelper';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import './RecentlyDeleted.css';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');

function RecentlyDeleted() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [deletedItems, setDeletedItems] = useState({ teams: [], graphics: [], players: [], bragCards: [] });
  const [currentSubscriptionPlan, setCurrentSubscriptionPlan] = useState(
    user?.subscriptionPlan
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    window.scroll(0, 0);
    fetchDeletedItems();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDeletedItems = async () => {
    try {
      const response = await trashAPI.getRecentlyDeleted();
      setDeletedItems(response.data);

      // Fetch current subscription plan from API
      const membershipResponse = await fetch(
        `${API_BASE_URL}/api/auth/membership`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const membershipData = await membershipResponse.json();
      setCurrentSubscriptionPlan(membershipData.subscriptionPlan);
    } catch (err) {
      setError('Failed to load deleted items');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreTeam = async (teamId) => {
    setRestoring(`team-${teamId}`);
    try {
      await trashAPI.restoreTeam(teamId);
      await fetchDeletedItems();
      setRestoring(null);
    } catch (err) {
      setError('Failed to restore team');
      setRestoring(null);
    }
  };

  const handleRestoreGraphic = async (graphicId) => {
    setRestoring(`graphic-${graphicId}`);
    try {
      await trashAPI.restoreGraphic(graphicId);
      await fetchDeletedItems();
      setRestoring(null);
    } catch (err) {
      setError('Failed to restore graphic');
      setRestoring(null);
    }
  };

  const handleRestorePlayer = async (playerId) => {
    setRestoring(`player-${playerId}`);
    try {
      await trashAPI.restorePlayer(playerId);
      await fetchDeletedItems();
      setRestoring(null);
    } catch (err) {
      setError('Failed to restore player');
      setRestoring(null);
    }
  };

  const handleRestoreBragCard = async (cardId) => {
    setRestoring(`bragcard-${cardId}`);
    try {
      await trashAPI.restoreBragCard(cardId);
      await fetchDeletedItems();
      setRestoring(null);
    } catch (err) {
      setError('Failed to restore BragCard');
      setRestoring(null);
    }
  };

  const getTimeRemaining = (deletedAt) => {
    const deleted = new Date(deletedAt);
    const expiry = new Date(deleted.getTime() + 3 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const remaining = expiry - now;

    if (remaining <= 0) return 'Expired';

    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );

    if (days > 0) {
      return `${days} day${days === 1 ? '' : 's'} ${hours}h remaining`;
    }
    return `${hours} hour${hours === 1 ? '' : 's'} remaining`;
  };

  if (loading) {
    return (
      <div className="recently-deleted-container">
        <div className="recently-deleted-content">
          <div className="loading-state">Loading deleted items...</div>
        </div>
      </div>
    );
  }

  const hasDeletedItems =
    deletedItems.teams.length > 0 || deletedItems.graphics.length > 0 || deletedItems.players.length > 0 || deletedItems.bragCards.length > 0;

  return (
    <>
      <Navbar />
      <div className="recently-deleted-container">
        <div className="recently-deleted-content">
          <button onClick={() => navigate('/')} className="back-button">
            ← Back to Home
          </button>
          <div className="page-header">
            <h1>Recently Deleted</h1>
            <p>
              Items deleted in the last 3 days. Restore them before they're
              permanently deleted.
            </p>
          </div>

          {error && <div className="error-message">{error}</div>}

          {!hasDeletedItems ? (
            <div className="empty-trash">
              <div className="empty-icon">✨</div>
              <h3>No Recently Deleted Items</h3>
              <p>
                Items you delete will appear here for 3 days before being
                permanently removed.
              </p>
            </div>
          ) : (
            <>
              {/* Deleted Players */}
              {deletedItems.players.length > 0 && (
                <div className="deleted-section">
                  <h2>Deleted Players ({deletedItems.players.length})</h2>
                  <div className="deleted-items-grid">
                    {deletedItems.players.map((player) => {
                      const timeRemaining = getTimeRemaining(player.deletedAt);
                      return (
                        <div key={player.id} className="deleted-item-card">
                          <div className="deleted-item-header" style={{ background: 'linear-gradient(135deg, #1a1e2e 0%, #2d3561 100%)', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ fontSize: '32px' }}>
                              {player.sport === 'softball' ? '🥎' : '⚾'}
                            </div>
                            <div style={{ color: '#fff', fontWeight: 800, fontSize: '16px' }}>
                              {player.firstName} {player.lastName}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                              {player.sport}{player.playingLevel ? ` · ${player.playingLevel}` : ''}
                            </div>
                          </div>
                          <div className="deleted-item-body">
                            <div className="deleted-time-remaining">
                              ⏰ {timeRemaining}
                            </div>
                            <button
                              className="btn btn-primary btn-sm"
                              style={{ width: '100%', marginTop: '10px' }}
                              onClick={() => handleRestorePlayer(player.id)}
                              disabled={restoring === `player-${player.id}` || timeRemaining === 'Expired'}
                            >
                              {restoring === `player-${player.id}` ? 'Restoring…' : '↩ Restore Player'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Deleted Teams */}
              {deletedItems.teams.length > 0 && (
                <div className="deleted-section">
                  <h2>⚾🥎 Deleted Teams ({deletedItems.teams.length})</h2>
                  <div className="deleted-items-grid">
                    {deletedItems.teams.map((team) => {
                      const isOrgTeam = team.organizationTeam === true;
                      const isLockedOrgTeam =
                        isOrgTeam &&
                        team.deletedAt &&
                        currentSubscriptionPlan !== 'organization';
                      const timeRemaining = getTimeRemaining(team.deletedAt);

                      return (
                        <div
                          key={team.id}
                          className={`deleted-item-card ${
                            isLockedOrgTeam ? 'org-team-deleted' : ''
                          }`}
                        >
                          <div
                            className="deleted-item-header"
                            style={{
                              background: `linear-gradient(135deg, ${team.primaryColor} 0%, ${team.secondaryColor} 100%)`,
                            }}
                          >
                            <div className="deleted-logo">
                              {team.logoUrl ? (
                                <img
                                  src={getLogoSrc(team.logoUrl, API_BASE_URL)}
                                  alt={`${team.name} logo`}
                                />
                              ) : (
                                <span className="deleted-logo-fallback">
                                  ⚾
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="deleted-item-body">
                            <h3>{team.name}</h3>
                            <div className="deleted-item-details">
                              <span>{team.ageGroup}</span>
                              <span>•</span>
                              <span>{team.season}</span>
                            </div>
                            <div className="time-remaining">
                              ⏰ {timeRemaining}
                            </div>

                            {isLockedOrgTeam ? (
                              <>
                                <div className="renewal-required-badge">
                                  ⚠️ Renewal Required
                                </div>
                                <button
                                  className="btn-restore-disabled"
                                  disabled
                                  title="Renew your Organization subscription to restore this team"
                                >
                                  🔒 Renew Subscription to Restore
                                </button>
                              </>
                            ) : (
                              <button
                                className="btn-restore"
                                onClick={() => handleRestoreTeam(team.id)}
                                disabled={restoring === `team-${team.id}`}
                              >
                                {restoring === `team-${team.id}`
                                  ? 'Restoring...'
                                  : '↶ Restore Team'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Deleted BragCards */}
              {deletedItems.bragCards.length > 0 && (
                <div className="deleted-section">
                  <h2>Deleted BragCards ({deletedItems.bragCards.length})</h2>
                  <div className="deleted-items-grid">
                    {deletedItems.bragCards.map((card) => {
                      const timeRemaining = getTimeRemaining(card.deletedAt);
                      return (
                        <div key={card.id} className="deleted-item-card">
                          <div
                            className="deleted-item-header"
                            style={{
                              background: `linear-gradient(135deg, ${card.primaryColor} 0%, ${card.accentColor} 100%)`,
                            }}
                          >
                            <div className="graphic-type-badge">⭐</div>
                          </div>
                          <div className="deleted-item-body">
                            <h3>{card.playerName}</h3>
                            {card.milestoneLabel && (
                              <div className="deleted-item-details">
                                <span>{card.milestoneLabel}</span>
                              </div>
                            )}
                            <div className="time-remaining">
                              ⏰ {timeRemaining}
                            </div>
                            <button
                              className="btn-restore"
                              onClick={() => handleRestoreBragCard(card.id)}
                              disabled={restoring === `bragcard-${card.id}` || timeRemaining === 'Expired'}
                            >
                              {restoring === `bragcard-${card.id}`
                                ? 'Restoring...'
                                : '↶ Restore BragCard'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Deleted Graphics */}
              {deletedItems.graphics.length > 0 && (
                <div className="deleted-section">
                  <h2>Deleted Graphics ({deletedItems.graphics.length})</h2>
                  <div className="deleted-items-grid">
                    {deletedItems.graphics.map((graphic) => {
                      // Org graphics are never locked - they can be restored by anyone with access
                      const isLockedOrgGraphic = false;
                      const timeRemaining = getTimeRemaining(graphic.deletedAt);

                      return (
                        <div
                          key={graphic.id}
                          className={`deleted-item-card ${
                            isLockedOrgGraphic ? 'org-team-deleted' : ''
                          }`}
                        >
                          <div
                            className="deleted-item-header"
                            style={{
                              background: `linear-gradient(135deg, ${graphic.Team.primaryColor} 0%, ${graphic.Team.secondaryColor} 100%)`,
                            }}
                          >
                            <div className="graphic-type-badge">
                              {graphic.templateType === 'preview' && '📅'}
                              {graphic.templateType === 'recap' && '🏆'}
                              {graphic.templateType === 'tryout' && '🎯'}
                              {graphic.templateType === 'highlight' && '⭐'}
                            </div>
                          </div>

                          <div className="deleted-item-body">
                            <h3>
                              {graphic.templateType === 'tryout'
                                ? `${graphic.graphicData.homeTeam} Tryouts`
                                : graphic.templateType === 'highlight'
                                  ? graphic.graphicData.highlightPlayer
                                  : `${graphic.graphicData.homeTeam} vs ${graphic.graphicData.awayTeam}`}
                            </h3>
                            <div className="deleted-item-details">
                              <span>{graphic.Team.name}</span>
                            </div>
                            <div className="time-remaining">
                              ⏰ {timeRemaining}
                            </div>

                            {isLockedOrgGraphic ? (
                              <>
                                <div className="renewal-required-badge">
                                  ⚠️ Renewal Required
                                </div>
                                <button
                                  className="btn-restore-disabled"
                                  disabled
                                  title="Renew your Organization subscription to restore this graphic"
                                >
                                  🔒 Renew Subscription to Restore
                                </button>
                              </>
                            ) : (
                              <button
                                className="btn-restore"
                                onClick={() => handleRestoreGraphic(graphic.id)}
                                disabled={restoring === `graphic-${graphic.id}`}
                              >
                                {restoring === `graphic-${graphic.id}`
                                  ? 'Restoring...'
                                  : '↶ Restore Graphic'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default RecentlyDeleted;
